import React, {
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import Cookies from 'js-cookie';
import {
    buildShopUsDiscountUrl,
    getValidReferralCode,
} from '../utils/shopUsReferral';

type ReferralContext = {
    code?: string;
    prepareAnchor?: (anchor: HTMLAnchorElement) => void;
};

export const ReferralCodeContext = createContext<ReferralContext>({});

interface Props {
    enabled: boolean;
    pathname: string;
    search: string;
    children: React.ReactNode;
}

type ReferralState = {
    pathname: string;
    search: string;
    code: string | undefined;
};

// Google decorates the discount URL after activation. Ignore only _gl when
// checking ownership, and carry its latest value back to the original URL.
function recoverOriginalHref(
    current: string | null,
    original: string,
    applied: string
): string | undefined {
    if (current === applied) return original;
    if (!current) return undefined;
    try {
        const url = new URL(current, applied);
        const expected = new URL(applied);
        const linker = url.searchParams.get('_gl');
        url.searchParams.delete('_gl');
        expected.searchParams.delete('_gl');
        if (url.href !== expected.href) return undefined;
        const restored = new URL(original, applied);
        if (linker !== null) restored.searchParams.set('_gl', linker);
        return restored.href;
    } catch {
        return undefined;
    }
}

export const ReferralCodeProvider: React.FC<Props> = ({
    enabled,
    pathname,
    search,
    children,
}) => {
    // Start with the same links during SSR and the first client render.
    const [referral, setReferral] = useState<ReferralState>();
    const originalHrefs = useRef(
        new Map<HTMLAnchorElement, { original: string; applied: string }>()
    );

    useEffect(() => {
        const fromUrl = getValidReferralCode(
            new URLSearchParams(search).get('mbsy')
        );
        setReferral({
            pathname,
            search,
            code: fromUrl ?? getValidReferralCode(Cookies.get('promocode')),
        });
    }, [pathname, search]);

    // Never expose the previous route's code while its replacement resolves.
    const code =
        enabled &&
        referral?.pathname === pathname &&
        referral?.search === search
            ? referral.code
            : undefined;

    const prepareAnchor = useCallback(
        (anchor: HTMLAnchorElement) => {
            if (!code) return;

            const current = anchor.getAttribute('href');
            if (!current) return;
            const previous = originalHrefs.current.get(anchor);
            const original =
                previous?.applied === current ? previous.original : current;
            const next = buildShopUsDiscountUrl(original, code);
            if (next === current) return;

            originalHrefs.current.set(anchor, { original, applied: next });
            anchor.setAttribute('href', next);
        },
        [code]
    );

    useEffect(() => {
        if (!code) return;

        // Delegation also covers anchors inserted or patched outside React.
        // The browser still performs the navigation using the prepared href.
        const onActivate = (event: Event) => {
            const anchor = event
                .composedPath()
                .find((node) => node instanceof HTMLAnchorElement);
            if (anchor instanceof HTMLAnchorElement) prepareAnchor(anchor);
        };
        // Prepare pointer links before the browser snapshots a context-menu URL;
        // click also covers keyboard activation without a preceding pointer.
        const events = [
            'pointerdown',
            'click',
            'auxclick',
            'contextmenu',
        ] as const;
        events.forEach((event) =>
            document.addEventListener(event, onActivate, true)
        );
        const originals = originalHrefs.current;

        return () => {
            events.forEach((event) =>
                document.removeEventListener(event, onActivate, true)
            );
            for (const [anchor, { original, applied }] of originals) {
                const restored = recoverOriginalHref(
                    anchor.getAttribute('href'),
                    original,
                    applied
                );
                if (restored !== undefined)
                    anchor.setAttribute('href', restored);
            }
            originals.clear();
        };
    }, [code, prepareAnchor]);

    const context = useMemo(
        () => ({ code, prepareAnchor: code ? prepareAnchor : undefined }),
        [code, prepareAnchor]
    );

    return (
        <ReferralCodeContext.Provider value={context}>
            {children}
        </ReferralCodeContext.Provider>
    );
};
